/**
 * Shared helpers and types for headless combat simulations.
 * Used by balanceSimulation, multiEncounterSimulation, and tests.
 */

import { ENEMIES, EnemyInstance, createEnemyInstance } from "../data/enemies";
import { EnemyId } from "./runState";
import { PerkId } from "../data/perks";
import { SKILLS, SkillId } from "../data/skills";
import { CombatAction, CombatActionOption, CombatSnapshot } from "./combat";
import { CombatEvent } from "./combatEvents";
import { applyPerk, newRun, RunState, SkillLevel } from "./runState";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

/** Context passed to action policy functions for decision-making. */
export type ActionPolicyContext = {
  snapshot: CombatSnapshot;
  options: CombatActionOption[];
  turnNumber: number;
  seed: number;
  rng: () => number;
};

/** A function that chooses a combat action given the current context. */
export type ActionPolicy = (context: ActionPolicyContext) => CombatAction;

/** Player loadout configuration for simulations. */
export type PlayerLoadout = {
  hp?: number;
  maxHp?: number;
  atk?: number;
  skills?: SkillId[] | Record<SkillId, number>;
  perks?: PerkId[];
};

/** Enemy group specification for building encounters. */
export type EnemyGroup = {
  id: EnemyId;
  count?: number;
};

/** A recorded combat event with metadata. */
export type BalanceEvent = {
  seed: number;
  turn: number;
  actor: "player" | "enemy";
  skillId?: SkillId;
  resourceCost?: number;
  event: CombatEvent;
};

/** Outcome of a simulated combat. */
export type SimulationOutcome = "win" | "loss" | "stalemate";

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/** Default max turns before a combat is considered a stalemate. */
export const DEFAULT_MAX_TURNS = 25;

/**
 * Create a deterministic RNG using a linear congruential generator.
 * Same seed always produces the same sequence.
 */
export const createDeterministicRng = (seed: number) => {
  let current = seed >>> 0;
  return () => {
    current = (current * 1664525 + 1013904223) >>> 0;
    return current / 4294967296;
  };
};

/**
 * Build a RunState from a player loadout configuration.
 * Starts with defaults from newRun() and applies overrides.
 */
export const buildRun = (loadout: PlayerLoadout): RunState => {
  const run = newRun();
  if (typeof loadout.hp === "number") {
    run.player.hp = loadout.hp;
  }
  if (typeof loadout.maxHp === "number") {
    run.player.maxHp = loadout.maxHp;
    run.player.hp = Math.min(run.player.hp, run.player.maxHp);
  }
  if (typeof loadout.atk === "number") {
    run.player.atk = loadout.atk;
  }
  if (loadout.skills) {
    if (Array.isArray(loadout.skills)) {
      // Array format: set all skills to level 1
      run.player.skills = loadout.skills.reduce<Partial<Record<SkillId, SkillLevel>>>(
        (acc, skillId) => {
          acc[skillId] = 1 as SkillLevel;
          return acc;
        },
        {}
      );
    } else {
      // Record format: convert numbers to SkillLevel
      run.player.skills = Object.entries(loadout.skills).reduce<
        Partial<Record<SkillId, SkillLevel>>
      >((acc, [skillId, level]) => {
        acc[skillId as SkillId] = Math.min(Math.max(1, level), 5) as SkillLevel;
        return acc;
      }, {});
    }
  }
  if (loadout.perks) {
    for (const perkId of loadout.perks) {
      applyPerk(run, perkId);
    }
  }
  return run;
};

/**
 * Build enemy instances from group specifications.
 * Supports multiple enemies of the same type with auto-suffixes.
 */
export const buildEnemies = (groups: EnemyGroup[]): EnemyInstance[] => {
  const instances: EnemyInstance[] = [];
  for (const group of groups) {
    const def = ENEMIES[group.id as EnemyId];
    const count = Math.max(1, group.count ?? 1);
    for (let index = 0; index < count; index += 1) {
      const suffix = count > 1 ? `#${index + 1}` : undefined;
      instances.push(createEnemyInstance(def, suffix));
    }
  }
  return instances;
};

/**
 * Create an empty map of skill usage/value counters.
 */
export const emptySkillMap = (): Record<SkillId, number> => {
  const map: Record<SkillId, number> = Object.create(null);
  for (const skillId of Object.keys(SKILLS) as SkillId[]) {
    map[skillId] = 0;
  }
  return map;
};
